import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaCommentDots, FaUserFriends, FaUser, FaInfoCircle, FaSignOutAlt, FaFileAlt, FaUserShield, FaBullhorn, FaMugHot, FaGlobeAmericas, FaEnvelope, FaCrown } from 'react-icons/fa';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import './Navbar.css';

function Navbar({ user, navbarColor = '#000000', navbarImage = null, hasUnreadMessages = false, clearUnreadNotification, skin }) {
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isWorker, setIsWorker] = useState(false);
  const [userViewMode, setUserViewMode] = useState(localStorage.getItem('staffUserViewMode') === 'true');

  useEffect(() => {
    checkStaffStatus();
  }, [user]);

  useEffect(() => {
    // Listen for changes to user view mode from AdminManagement
    const handleViewModeChange = () => {
      setUserViewMode(localStorage.getItem('staffUserViewMode') === 'true');
    };
    window.addEventListener('staffViewModeChanged', handleViewModeChange);
    return () => window.removeEventListener('staffViewModeChanged', handleViewModeChange);
  }, []);

  const checkStaffStatus = async () => {
    try {
      const userId = localStorage.getItem('userId');
      if (userId) {
        const adminDoc = await getDoc(doc(db, 'admins', userId));
        if (adminDoc.exists()) {
          setIsAdmin(true);
          setIsWorker(false);
        } else {
          // Check if worker
          const workerDoc = await getDoc(doc(db, 'workers', userId));
          setIsWorker(workerDoc.exists());
          setIsAdmin(false);
        }
      }
    } catch (error) {
      console.error('Error checking staff status:', error);
    }
  };

  const isActive = (path) => location.pathname === path ? 'active' : '';

  const handleMessagesClick = () => {
    if (clearUnreadNotification) {
      clearUnreadNotification();
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);

      // Preserve custom images before clearing localStorage
      const userId = localStorage.getItem('userId');
      const bgImage = localStorage.getItem(`yapper_bg_image_${userId}`);
      const navbarImage = localStorage.getItem(`yapper_navbar_image_${userId}`);

      localStorage.clear();

      // Restore custom images after clearing
      if (bgImage) localStorage.setItem(`yapper_bg_image_${userId}`, bgImage);
      if (navbarImage) localStorage.setItem(`yapper_navbar_image_${userId}`, navbarImage);

      window.location.reload();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Convert hex to rgba
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const navbarStyle = navbarImage
    ? {
        backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 70%, rgba(0,0,0,0) 100%), url(${navbarImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top'
      }
    : {
        background: `linear-gradient(180deg, ${hexToRgba(navbarColor, 0.9)} 0%, ${hexToRgba(navbarColor, 0.7)} 70%, ${hexToRgba(navbarColor, 0)} 100%)`
      };

  return (
    <nav className="navbar" style={navbarStyle}>
      <div className="nav-container">
        {skin === 'moe' ? (
          <Link to="/" className="nav-logo moe-navbar-title">
            <span className="moe-logo">Yapp<span>♡</span></span>
            <span className="moe-tag">✦ つながる世界 · connect the world ✦</span>
          </Link>
        ) : (
          <Link to="/" className="nav-logo yap-logo">
            <FaGlobeAmericas className="yap-mark" />
            <span className="yap-word">Yapp</span>
          </Link>
        )}

        <ul className="nav-menu">
          <li className={`nav-item ${isActive('/')}`}>
            <Link to="/" className="nav-link" title="Globe">
              <FaGlobeAmericas />
              <span className="nav-indicator" />
            </Link>
          </li>
          <li className={`nav-item ${isActive('/messages')}`}>
            <Link to="/messages" className="nav-link" onClick={handleMessagesClick} title="Conversations">
              <FaCommentDots className={hasUnreadMessages ? 'has-unread' : ''} />
              {hasUnreadMessages && <span className="nav-dot" />}
              <span className="nav-indicator" />
            </Link>
          </li>
          <li className={`nav-item ${isActive('/friends')}`}>
            <Link to="/friends" className="nav-link" title="Friends">
              <FaUserFriends />
              <span className="nav-indicator" />
            </Link>
          </li>
          <li className={`nav-item ${isActive('/profile')}`}>
            <Link to="/profile" className="nav-link" title="Profile">
              <FaUser />
              <span className="nav-indicator" />
            </Link>
          </li>
          <li className={`nav-item ${isActive('/announcements')}`}>
            <Link to="/announcements" className="nav-link" title="Announcements">
              <FaBullhorn />
              <span className="nav-indicator" />
            </Link>
          </li>
          <li className={`nav-item ${isActive('/support')}`}>
            <Link to="/support" className="nav-link" title="Support">
              <FaMugHot />
              <span className="nav-indicator" />
            </Link>
          </li>
          <li className={`nav-item ${isActive('/about')}`}>
            <Link to="/about" className="nav-link" title="About">
              <FaInfoCircle />
              <span className="nav-indicator" />
            </Link>
          </li>
          {(isAdmin || isWorker) && !userViewMode && (
            <>
              <li className={`nav-item ${isActive('/reports')}`}>
                <Link to="/reports" className={`nav-link ${isAdmin ? 'admin-link' : 'worker-link'}`} title="Reports">
                  <FaFileAlt />
                  <span className="nav-indicator" />
                </Link>
              </li>
              <li className={`nav-item ${isActive('/contact-messages')}`}>
                <Link to="/contact-messages" className={`nav-link ${isAdmin ? 'admin-link' : 'worker-link'}`} title="Contact Messages">
                  <FaEnvelope />
                  <span className="nav-indicator" />
                </Link>
              </li>
              <li className={`nav-item ${isActive('/admin-management')}`}>
                <Link to="/admin-management" className={`nav-link ${isAdmin ? 'admin-link' : 'worker-link'}`} title="Staff">
                  <FaUserShield />
                  <span className="nav-indicator" />
                </Link>
              </li>
              {isAdmin && (
                <li className={`nav-item ${isActive('/pending-activations')}`}>
                  <Link to="/pending-activations" className="nav-link admin-link" title="Pending Activations">
                    <FaCrown />
                    <span className="nav-indicator" />
                  </Link>
                </li>
              )}
            </>
          )}
          <li className="nav-item nav-divider" aria-hidden="true" />
          <li className="nav-item">
            <button onClick={handleLogout} className="nav-link logout-btn" title="Logout">
              <FaSignOutAlt />
            </button>
          </li>
        </ul>
      </div>
    </nav>
  );
}

export default Navbar;
